<script setup lang="ts">
/**
 * ShareDialog — Shareable URL with copy to clipboard
 *
 * Props:
 *   visible — controls dialog visibility
 *   fitID — the fit ID to share
 *   fitName — the fit name for display
 *
 * Events:
 *   close — when dialog should close
 */
import { ref, computed, watch } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { saveFit } from '../../composables/useApi';

const props = defineProps<{
  visible: boolean;
  fitId: number | null;
  fitName: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const store = useFitStore();
const copied = ref(false);
const publishing = ref(false);

const shareUrl = computed(() => {
  if (!props.fitId) return '';
  return `${window.location.origin}/?fit=${props.fitId}`;
});

watch(() => props.visible, (v) => {
  if (v) {
    copied.value = false;
  }
});

async function handleCopy(): Promise<void> {
  if (store.activeFit?.isPublic !== 1) {
    if (store.activeFit?.canEdit === false) {
      store.addToast('warning', 'This private fit is read-only. Copy it before sharing.');
      return;
    }
    publishing.value = true;
    try {
      await saveFit(props.fitId!, props.fitName);
      await store.refreshActiveFit();
    } catch {
      publishing.value = false;
      return;
    }
    publishing.value = false;
  }
  try {
    await navigator.clipboard.writeText(shareUrl.value);
    copied.value = true;
    store.addToast('success', 'Link copied to clipboard');
    setTimeout(() => { copied.value = false; }, 2000);
  } catch {
    store.addToast('error', 'Failed to copy to clipboard');
  }
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
    <div v-if="visible" class="modal-overlay" @click.self="emit('close')" @keydown="handleKeydown">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">Share Fitting</h3>
          <button class="modal-close" @click="emit('close')">&times;</button>
        </div>

        <div class="modal-body">
          <p class="share-label">Share link for "{{ fitName }}":</p>
          <div class="url-row">
            <input
              class="url-input"
              :value="shareUrl"
              readonly
              @focus="($event.target as HTMLInputElement).select()"
            />
            <button class="btn btn--primary" :disabled="publishing" @click="handleCopy">
              {{ publishing ? 'Publishing...' : (copied ? 'Copied!' : 'Copy') }}
            </button>
          </div>
          <p class="share-hint">{{ store.activeFit?.isPublic === 1 ? 'Anyone with this link can view the fitting.' : 'Copying this link will publish the fit so it can be viewed.' }}</p>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 440px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.15s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 16px;
}

.share-label {
  font-size: 0.85rem;
  color: var(--text-primary);
  margin: 0 0 10px;
}

.url-row {
  display: flex;
  gap: 8px;
}

.url-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-darker);
  color: var(--text-primary);
  font-size: 0.78rem;
  font-family: 'Consolas', 'Monaco', monospace;
}

.url-input:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.share-hint {
  font-size: 0.72rem;
  color: var(--text-secondary);
  margin: 10px 0 0;
}

.btn {
  padding: 6px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.btn--primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: #fff;
}

.btn--primary:hover {
  background: #3a8eef;
}

/* Modal transitions */
.modal-enter-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-dialog {
  animation: modal-in 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@keyframes modal-in {
  from {
    transform: translateY(-8px) scale(0.97);
  }
  to {
    transform: translateY(0) scale(1);
  }
}
</style>
